import { Howler } from 'howler';
import { Container, Sprite, Texture, Text } from 'pixi.js';
import screenfull from 'screenfull';
import { ResizeData } from '../../helpers/ResizeData';
import { BASE_FONT, IS_IOS, MainSignals, TickCallData } from '../../settings/constants';
import { TweenMax, Power1 } from 'gsap';
import { SCENE_LENGTH, WALK_END } from '../../settings/positions';
import { continueScroll, prevScroll } from '../../helpers/Scroll';

const SPRITE_SCALE = 0.7;

const SOUND_STORAGE_ID = 'bozena-nemcova-music';
const SOUND_ON = 'on';
const SOUND_OFF = 'off';
export default class UI extends Container {
  signals: MainSignals;

  btnInfo: Sprite;
  btnSound: Sprite;
  btnFullscreen?: Sprite;
  link: Container;
  continueBtn: Sprite;
  backBtn: Sprite;

  btns: Sprite[] = [];

  soundMuted = false;
  linkActive = false;
  continueActive = true;
  backActive = false;

  constructor(signals: MainSignals) {
    super();

    // vars
    this.signals = signals;

    // parts
    if (!IS_IOS && screenfull.isEnabled) {
      this.btnFullscreen = this.setBtnFullscreen();
    }
    this.btnSound = this.setBtnSound();
    this.btnInfo = this.setBtnInfo();
    this.link = this.setLink();
    this.continueBtn = this.setContinueBtn();
    this.backBtn = this.setBackBtn();

    // listeners
    signals.resizeCall.add(this.resizeCall, this);
    signals.tickCall.add(this.tickCall, this);
  }

  // --------------------------------------------------------------
  // Helpers
  // --------------------------------------------------------------

  getButton(textureName: string): Sprite {
    const btn = Sprite.from(textureName);
    btn.interactive = true;
    btn.buttonMode = true;
    btn.anchor.set(0.5);
    this.addChild(btn);
    this.btns.push(btn);
    return btn;
  }

  // --------------------------------------------------------------
  // Info
  // --------------------------------------------------------------

  setBtnInfo(): Sprite {
    const btn = this.getButton('btn-info.png');
    btn.on('pointerdown', this.handleInfoDown.bind(this));
    return btn;
  }

  handleInfoDown(): void {
    const aboutLayer = document.getElementById('o-projektu');
    if (aboutLayer) {
      aboutLayer.style.display = 'flex';
      aboutLayer.style.opacity = '0';
      TweenMax.to(aboutLayer, 0.4, { opacity: 1, ease: Power1.easeInOut });
    }
  }

  // --------------------------------------------------------------
  // Sound
  // --------------------------------------------------------------

  setBtnSound(): Sprite {
    // init sound
    this.soundMuted = window.localStorage.getItem(SOUND_STORAGE_ID) === SOUND_OFF;
    Howler.mute(this.soundMuted);

    // set btn
    const btn = this.getButton(this.getSoundTextureName());
    btn.on('pointerdown', this.handleSoundDown.bind(this));
    return btn;
  }

  getSoundTextureName(): string {
    return this.soundMuted ? 'btn-snd-off.png' : 'btn-snd-on.png';
  }

  handleSoundDown(): void {
    this.soundMuted = !this.soundMuted;
    Howler.mute(this.soundMuted);
    this.btnSound.texture = Texture.from(this.getSoundTextureName());
    window.localStorage.setItem(SOUND_STORAGE_ID, this.soundMuted ? SOUND_OFF : SOUND_ON);
  }

  // --------------------------------------------------------------
  // Fullscreen
  // --------------------------------------------------------------

  setBtnFullscreen(): Sprite {
    // set switch listener
    if (screenfull.isEnabled) {
      screenfull.on('change', this.handleFullscreen.bind(this));
    }

    // set btn
    const btn = this.getButton('btn-max.png');
    btn.on('pointerdown', this.handleFullscreenDown.bind(this));
    return btn;
  }

  handleFullscreenDown(): void {
    if (screenfull.isEnabled) {
      const element = document.documentElement;
      if (element) {
        screenfull.toggle(element);
      }
    }
  }

  handleFullscreen(): void {
    if (this.btnFullscreen) {
      this.btnFullscreen.texture = Texture.from(
        screenfull.isEnabled && screenfull.isFullscreen ? 'btn-min.png' : 'btn-max.png',
      );
    }
  }

  // --------------------------------------------------------------
  // Series link
  // --------------------------------------------------------------

  setLink(): Container {
    const link = new Container();
    link.visible = false;
    link.alpha = 0;
    link.on('pointerdown', this.handleLinkDown.bind(this));

    // bg
    const bg = Sprite.from('text-bg-light.png');
    bg.anchor.set(0.5);
    link.addChild(bg);

    // text
    const text = new Text('Více o filmové sérii Božena', { fontFamily: BASE_FONT, fontSize: 20, fill: 0x1a1b25 });
    text.anchor.set(0.5);
    link.addChild(text);

    bg.width = text.width + 50;
    bg.height = text.height + 30;

    this.addChild(link);
    return link;
  }

  handleLinkDown(): void {
    window.location.href = 'https://www.ceskatelevize.cz/porady/11346647457-bozena/';
  }

  checkLinkActive(value: number): void {
    this.setLinkActive(value >= WALK_END);
  }

  setLinkActive(value: boolean): void {
    if (this.linkActive !== value) {
      this.linkActive = value;

      TweenMax.killTweensOf(this.link);
      this.link.interactive = this.linkActive;
      this.link.buttonMode = this.linkActive;

      if (this.linkActive) {
        this.link.visible = true;
        TweenMax.to(this.link, 0.5, { delay: 0.2, alpha: 1, ease: Power1.easeInOut });
      } else {
        TweenMax.to(this.link, 0.2, {
          alpha: 0,
          onComplete: () => {
            this.link.visible = false;
          },
        });
      }
    }
  }

  // --------------------------------------------------------------
  // Back
  // --------------------------------------------------------------

  setBackBtn(): Sprite {
    const btn = Sprite.from('continue-btn.png');
    btn.anchor.set(0.5);
    btn.rotation = Math.PI;
    btn.interactive = false;
    btn.buttonMode = false;
    btn.visible = false;
    btn.on('pointerdown', this.handleBack.bind(this));

    this.addChild(btn);
    return btn;
  }

  handleBack(): void {
    prevScroll();
  }

  checkBackActive(value: number): void {
    this.setBackActive(value > SCENE_LENGTH * 2 * 0.333);
  }

  setBackActive(value: boolean): void {
    if (this.backActive !== value) {
      this.backActive = value;

      TweenMax.killTweensOf(this.backBtn);
      this.backBtn.interactive = this.backActive;
      this.backBtn.buttonMode = this.backActive;

      if (this.backActive) {
        this.backBtn.visible = true;
        TweenMax.to(this.backBtn, 0.5, { alpha: 1, ease: Power1.easeInOut });
      } else {
        TweenMax.to(this.backBtn, 0.2, {
          alpha: 0,
          onComplete: () => {
            this.backBtn.visible = false;
          },
        });
      }
    }
  }

  // --------------------------------------------------------------
  // Continue
  // --------------------------------------------------------------

  setContinueBtn(): Sprite {
    const btn = Sprite.from('continue-btn.png');
    btn.anchor.set(0.5);
    btn.interactive = true;
    btn.buttonMode = true;
    btn.on('pointerdown', this.handleContinue.bind(this));

    this.addChild(btn);
    return btn;
  }

  handleContinue(): void {
    continueScroll();
  }

  checkContinueActive(value: number): void {
    this.setContinueActive(value <= WALK_END);
  }

  setContinueActive(value: boolean): void {
    if (this.continueActive !== value) {
      this.continueActive = value;

      TweenMax.killTweensOf(this.continueBtn);
      this.continueBtn.interactive = this.continueActive;
      this.continueBtn.buttonMode = this.continueActive;

      if (this.continueActive) {
        this.continueBtn.visible = true;
        TweenMax.to(this.continueBtn, 0.5, { alpha: 1, ease: Power1.easeInOut });
      } else {
        TweenMax.to(this.continueBtn, 0.2, {
          alpha: 0,
          onComplete: () => {
            this.continueBtn.visible = false;
          },
        });
      }
    }
  }

  // --------------------------------------------------------------
  // Reisze
  // --------------------------------------------------------------

  resizeCall(data: ResizeData): void {
    const { scale } = data.stage;
    this.btns.forEach((btn, index) => {
      btn.scale.set(scale * SPRITE_SCALE);
      btn.x = 50 * scale;
      btn.y = (40 + 55 * index) * scale;
    });

    this.link.scale.set(scale);
    this.link.x = data.window.width - 300 * scale;
    this.link.y = data.window.height - 120 * scale;

    this.continueBtn.scale.set(0.8 * scale);
    this.continueBtn.x = data.window.width - 70 * scale;
    this.continueBtn.y = data.window.height - 45 * scale;

    this.backBtn.scale.set(0.8 * scale);
    this.backBtn.x = data.window.width - 70 * scale;
    this.backBtn.y = 45 * scale;
  }

  // --------------------------------------------------------------
  // Tick
  // --------------------------------------------------------------

  tickCall(data: TickCallData): void {
    this.checkLinkActive(data.scroll);
    this.checkContinueActive(data.scroll);
    this.checkBackActive(data.scroll);
  }
}
